import React, { memo, useEffect, useMemo, useState } from "react";
import axios from "axios";
import MyLoader from "../Spinner/MyLoader";
import SessionManager from "../Utils/SessionManager";
import myToast from "../Utils/MyToast";

const API_BASE = () =>
  `${process.env.REACT_APP_SERVER_NAME}${process.env.REACT_APP_TARGET_DIR}`;

const TASK_TYPES = [
  { id: "CREATE_PLAN", label: "Create lesson plan" },
  { id: "DELIVER_LESSON", label: "Deliver lesson" },
  { id: "REVIEW_PLAN", label: "Review plan" },
];

const PRIORITIES = ["Low", "Normal", "High"];

//Created By Cursor — getClass.php returns { class: [...] }; section may be "No" or array
function flattenClasses(classData = []) {
  const rows = [];
  let list = classData;
  if (list && !Array.isArray(list) && Array.isArray(list.class)) {
    list = list.class;
  }
  if (!Array.isArray(list)) return rows;

  list.forEach((cls) => {
    if (!cls) return;
    const sectionField = cls.section !== undefined ? cls.section : cls.sections;
    if (sectionField === "No" || sectionField === "no") {
      rows.push({
        class_id: cls.class_id,
        class_name: cls.class_name || cls.class_id,
        section_id: "No",
        section_name: "—",
      });
      return;
    }
    const sections = Array.isArray(sectionField) ? sectionField : [];
    if (sections.length === 0) {
      rows.push({
        class_id: cls.class_id,
        class_name: cls.class_name || cls.class_id,
        section_id: "0",
        section_name: "—",
      });
      return;
    }
    sections.forEach((sec) => {
      rows.push({
        class_id: sec.class_id || cls.class_id,
        class_name: sec.class_name || cls.class_name || cls.class_id,
        section_id: sec.section_id,
        section_name: sec.section_name || sec.section_id,
      });
    });
  });
  return rows;
}

//Created By Cursor
const MdlLessonPlanAssign = memo(function MdlLessonPlanAssign({
  show,
  onClose,
  onSaved,
  sessionId,
  staffList = [],
  classRows = [],
  subjects = [],
  defaultClassId = "",
  defaultSectionId = "",
  defaultSubjectId = "",
}) {
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    assignee_staff_id: "",
    task_title: "",
    task_type: "CREATE_PLAN",
    due_date: "",
    priority: "Normal",
    class_id: "",
    section_id: "",
    subject_id: "",
    task_instructions: "",
  });

  useEffect(() => {
    if (!show) return;
    setForm({
      assignee_staff_id: "",
      task_title: "",
      task_type: "CREATE_PLAN",
      due_date: "",
      priority: "Normal",
      class_id: defaultClassId || "",
      section_id: defaultSectionId || "",
      subject_id: defaultSubjectId || "",
      task_instructions: "",
    });
  }, [show, defaultClassId, defaultSectionId, defaultSubjectId]);

  const classOptions = useMemo(() => {
    const map = new Map();
    classRows.forEach((r) => {
      const key = `${r.class_id}::${r.section_id}`;
      if (!map.has(key)) map.set(key, r);
    });
    return Array.from(map.values());
  }, [classRows]);

  const subjectOptions = useMemo(() => {
    if (!form.class_id || !form.section_id) return subjects;
    return subjects.filter(
      (s) =>
        String(s.class_id) === String(form.class_id) &&
        String(s.section_id) === String(form.section_id)
    );
  }, [subjects, form.class_id, form.section_id]);

  const setField = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const schoolCode = SessionManager.getSchoolCode();
    const staff = SessionManager.getStaffDetails();
    if (!schoolCode) {
      myToast.notify_warning("School code is missing.");
      return;
    }
    if (!form.assignee_staff_id || !form.task_title.trim()) {
      myToast.notify_warning("Staff and task title are required.");
      return;
    }
    setBusy(true);
    try {
      const res = await axios.post(`${API_BASE()}assignLessonPlan.php`, {
        school_code: schoolCode,
        session_id: sessionId,
        assigned_by: staff?.staff_id || "",
        ...form,
        task_title: form.task_title.trim(),
      });
      if (res.data?.api_status === "OK") {
        myToast.notify_success("Task assigned.");
        if (onSaved) onSaved();
        if (onClose) onClose();
      } else {
        myToast.notify_warning(res.data?.api_remark || "Assign failed.");
      }
    } catch (err) {
      console.error(err);
      myToast.notify_warning("Assign failed.");
    } finally {
      setBusy(false);
    }
  };

  if (!show) return null;

  return (
    <div className="modal fade show d-block" style={{ background: "rgba(15,23,42,0.45)" }}>
      <div className="modal-dialog modal-lg modal-dialog-centered">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">Assign lesson plan task</h5>
            <button type="button" className="btn-close" onClick={onClose} aria-label="Close" />
          </div>
          <form onSubmit={handleSubmit}>
            <div className="modal-body">
              <div className="row g-3">
                <div className="col-md-6">
                  <label className="form-label">Staff *</label>
                  <select
                    className="form-select"
                    value={form.assignee_staff_id}
                    onChange={(e) => setField("assignee_staff_id", e.target.value)}
                    required
                  >
                    <option value="">Select staff</option>
                    {staffList.map((s) => (
                      <option key={s.staff_id} value={s.staff_id}>
                        {s.staff_name} ({s.staff_id})
                      </option>
                    ))}
                  </select>
                </div>
                <div className="col-md-6">
                  <label className="form-label">Task type</label>
                  <select
                    className="form-select"
                    value={form.task_type}
                    onChange={(e) => setField("task_type", e.target.value)}
                  >
                    {TASK_TYPES.map((t) => (
                      <option key={t.id} value={t.id}>
                        {t.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="col-12">
                  <label className="form-label">Task title *</label>
                  <input
                    className="form-control"
                    value={form.task_title}
                    onChange={(e) => setField("task_title", e.target.value)}
                    placeholder="e.g. Prepare lesson plan for Ch.5 Algebra"
                    required
                  />
                </div>
                <div className="col-md-4">
                  <label className="form-label">Class / Section</label>
                  <select
                    className="form-select"
                    value={`${form.class_id}::${form.section_id}`}
                    onChange={(e) => {
                      const [class_id, section_id] = e.target.value.split("::");
                      setField("class_id", class_id);
                      setField("section_id", section_id);
                      setField("subject_id", "");
                    }}
                  >
                    <option value="::">Optional</option>
                    {classOptions.map((r) => (
                      <option
                        key={`${r.class_id}-${r.section_id}`}
                        value={`${r.class_id}::${r.section_id}`}
                      >
                        {r.class_name} — {r.section_name}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="col-md-4">
                  <label className="form-label">Subject</label>
                  <select
                    className="form-select"
                    value={form.subject_id}
                    onChange={(e) => setField("subject_id", e.target.value)}
                  >
                    <option value="">Optional</option>
                    {subjectOptions.map((s) => (
                      <option key={s.subject_id} value={s.subject_id}>
                        {s.subject_name || s.book_name || s.subject_id}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="col-md-4">
                  <label className="form-label">Due date</label>
                  <input
                    type="date"
                    className="form-control"
                    value={form.due_date}
                    onChange={(e) => setField("due_date", e.target.value)}
                  />
                </div>
                <div className="col-md-4">
                  <label className="form-label">Priority</label>
                  <select
                    className="form-select"
                    value={form.priority}
                    onChange={(e) => setField("priority", e.target.value)}
                  >
                    {PRIORITIES.map((p) => (
                      <option key={p} value={p}>
                        {p}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="col-12">
                  <label className="form-label">Instructions</label>
                  <textarea
                    className="form-control"
                    rows={3}
                    value={form.task_instructions}
                    onChange={(e) => setField("task_instructions", e.target.value)}
                    placeholder="Notes for the assignee…"
                  />
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-light" onClick={onClose} disabled={busy}>
                Cancel
              </button>
              <button type="submit" className="btn btn-primary" disabled={busy}>
                {busy ? "Assigning…" : "Assign task"}
              </button>
            </div>
          </form>
        </div>
      </div>
      {busy ? <MyLoader /> : null}
    </div>
  );
});

export default MdlLessonPlanAssign;
export { flattenClasses };
